import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import type { ReactNode } from "react";
import type { GroupInfo } from "../../shared/api-contract";
import { useViewer } from "../ViewerProvider";
import { EditorStore } from "./EditorStore";
import { noteLoadKinds } from "./modelLoads";
import { makeAdapters, type DomainStateMap } from "./domains";
import { saveUi, type UiState } from "./ui";
import type { BindingCtx, DomainId, WriteOp } from "./types";

interface EditorContextValue {
  store: EditorStore;
  ctx: BindingCtx;
}

const EditorContext = createContext<EditorContextValue | null>(null);

/**
 * Instantiates the EditorStore once, seeds eager domains, wires viewer events
 * into store updates, and persists the `ui` slice. Provides a `ctx` that is
 * rebuilt whenever the selection changes so bindings always see a fresh
 * selection id. Mounts inside a ready ViewerProvider.
 */
export function EditorProvider({ children }: { children: ReactNode }) {
  const { client, catalogs } = useViewer();
  const ring = client.ring;

  const store = useMemo(() => {
    const s = new EditorStore(ring, makeAdapters());
    s.onError = (e) => console.warn("[editor] rpc error:", e.message);
    void s.seedEager();
    return s;
  }, [ring]);
  // The harnesses read the store through this, dev builds only. Set after
  // commit, not inside the memo: StrictMode runs a memo twice and keeps ONE
  // result, so assigning there could hand the harness the discarded store —
  // a store no viewer event ever reaches.
  useEffect(() => {
    if (import.meta.env.DEV) (window as unknown as { __editorStore: EditorStore }).__editorStore = store;
  }, [store]);

  // Wire viewer → store. Selection drives the material slice; model changes
  // refresh the lists; ui.followSelection auto-opens the Picking tab.
  useEffect(() => {
    const offSel = client.on("selectionChanged", ({ groupId }) => {
      store.setState("selection", groupId);
      if (groupId) {
        void ring.getGroupMaterialParams(groupId).then((p) => store.setState("material", p));
      } else {
        store.setState("material", null);
      }
      const ui = store.getState("ui") as UiState;
      // Advanced only: in Basic the right side is Fine-tune, and switching
      // `rightTab` there blanked it with no way back.
      if (ui.followSelection && groupId && ui.inspectorMode === "advanced") patchUiSlice(store, { rightTab: "materials" });
    });
    const offModels = client.on("modelsChanged", () => {
      void store.refresh("models");
      void store.refresh("groups");
      // Loading a piece re-applies the viewer's scene and post settings
      // without an event of their own; a slice seeded before that showed
      // "All effects: off" over a frame that was plainly post-processed.
      void store.refresh("postFX");
      void store.refresh("scene");
    });
    return () => {
      offSel();
      offModels();
    };
  }, [client, ring, store]);

  // Every part's kind as its piece loaded — the first time the groups slice
  // lists it. The configurator keeps a slot's kind from this, not from what
  // the part is wearing now (see `noteLoadKinds`).
  useEffect(() => {
    const note = () => noteLoadKinds(client, store.getState("groups") as GroupInfo[] | undefined);
    note();
    return store.subscribe("groups", note);
  }, [client, store]);

  // Persist ui whenever it changes.
  useEffect(() => {
    let prev = store.getState("ui");
    return store.subscribe("ui", () => {
      const next = store.getState("ui");
      if (next !== prev) {
        prev = next;
        saveUi(next as UiState);
      }
    });
  }, [store]);

  // NOTE: the store is intentionally NOT disposed on unmount. It lives for the
  // app session, and tying dispose() to a useEffect cleanup makes React
  // StrictMode (mount→unmount→remount) clear the slices of the store the tree
  // still uses. Poll timers are cleared when their last monitor row unmounts.

  // Rebuild ctx on selection change so material writes target the right group.
  const selectionId = useDomainStateOf<"selection">(store, "selection");
  const ctx = useMemo<BindingCtx>(
    () => ({
      ring,
      catalogs,
      read: (d: DomainId) => store.getState(d),
      selectionId,
    }),
    [ring, catalogs, store, selectionId],
  );

  const value = useMemo<EditorContextValue>(() => ({ store, ctx }), [store, ctx]);
  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

function useEditorContext(): EditorContextValue {
  const v = useContext(EditorContext);
  if (!v) throw new Error("useEditor* must be used within an EditorProvider");
  return v;
}

export function useEditorStore(): EditorStore {
  return useEditorContext().store;
}

export function useBindingCtx(): BindingCtx {
  return useEditorContext().ctx;
}

/** Subscribe to one domain; returns its (typed, stable-ref) slice value. */
function useDomainStateOf<K extends keyof DomainStateMap>(
  store: EditorStore,
  domain: K,
): DomainStateMap[K] {
  return useSyncExternalStore(
    useCallback((cb) => store.subscribe(domain, cb), [store, domain]),
    () => store.getState(domain) as DomainStateMap[K],
  );
}

export function useDomainState<K extends keyof DomainStateMap>(domain: K): DomainStateMap[K] {
  return useDomainStateOf(useEditorStore(), domain);
}

/**
 * Subscribe to several domains at once (a section reads many). Returns nothing —
 * the component re-renders when any subscribed domain changes; read live values
 * with `useEditorStore().getState(domain)` (or the `read` passed to rows). Uses
 * the store's global tick as a single primitive snapshot.
 */
export function useDomains(domains: DomainId[]): void {
  const store = useEditorStore();
  const key = domains.join(",");
  const subscribe = useCallback(
    (cb: () => void) => {
      const offs = domains.map((d) => store.subscribe(d, cb));
      return () => offs.forEach((o) => o());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store, key],
  );
  useSyncExternalStore(subscribe, () => store.getTick());
}

export function useMutate(): (domain: DomainId, op: WriteOp) => void {
  const store = useEditorStore();
  return useCallback((domain, op) => store.mutate(domain, op), [store]);
}

/** Register periodic refresh of a domain for the component's lifetime. */
export function usePoll(domain: DomainId, ms: number, active = true): void {
  const store = useEditorStore();
  useEffect(() => {
    if (!active) return;
    return store.addPollRef(domain, ms);
  }, [store, domain, ms, active]);
}

// ── ui slice helpers ──────────────────────────────────────────────────────

function patchUiSlice(store: EditorStore, patch: Partial<UiState>) {
  const cur = store.getState("ui") as UiState;
  store.setState("ui", { ...cur, ...patch });
}

/** Read the ui slice and a patcher for it. */
export function useUi(): [UiState, (patch: Partial<UiState>) => void] {
  const store = useEditorStore();
  const ui = useDomainState("ui");
  const patch = useCallback((p: Partial<UiState>) => patchUiSlice(store, p), [store]);
  return [ui, patch];
}

/** Toggle/read a section's collapsed flag (keyed `${tab}.${section}`). */
export function useCollapsed(): [Record<string, boolean>, (key: string, v: boolean) => void] {
  const store = useEditorStore();
  const [ui, patchUi] = useUi();
  // Read the map at CALL time, not render time: "Collapse other sections"
  // calls this once per sibling in one tick, and a render-time copy made every
  // call overwrite the one before — only the last sibling ever collapsed.
  const set = useCallback(
    (key: string, v: boolean) => patchUi({ collapsed: { ...(store.getState("ui") as UiState).collapsed, [key]: v } }),
    [store, patchUi],
  );
  return [ui.collapsed, set];
}

const noticeRef = { current: null as null | ((m: string) => void) };
/** Imperative error/notice sink (wired by the shell toast, optional). */
export function setNoticeSink(fn: ((m: string) => void) | null) {
  noticeRef.current = fn;
}
export function notice(message: string) {
  noticeRef.current?.(message);
}
